import React, { useCallback, useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CsvExport } from "@/components/CsvExport";
import { InvoiceUploadSheet, openTallyPdf } from "@/pages/invoices/InvoiceUploadSheet";
import { InvoiceRow } from "@/pages/invoices/InvoiceRow";
import { printInvoice } from "@/pages/invoices/invoicePrint";
import { UploadSimple, ArrowsClockwise, FileArrowDown, Printer } from "@phosphor-icons/react";

/**
 * Invoices module — Tally-first workflow.
 *
 * Invoices are generated in Tally and then uploaded here as PDFs for AR
 * tracking, payment reconciliation and aging. Legacy internally-generated
 * invoices remain viewable / editable but new records are upload-only.
 */
export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [company, setCompany] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get("/invoices"),
      api.get("/projects"),
      api.get("/settings/company"),
    ]);
    setRows(a.data); setProjects(b.data); setCompany(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = useCallback(async (inv) => {
    // For Tally uploads, open the original uploaded PDF; otherwise fetch the
    // internally-generated PDF via the /invoices/:id/pdf endpoint.
    if (inv.source === "tally" && inv.attachment_id) {
      openTallyPdf(inv);
      return;
    }
    const res = await fetch(`${API}/invoices/${inv.id}/pdf`, { credentials: "include" });
    if (!res.ok) { toast.error("PDF download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${inv.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  }, []);

  const del = useCallback(async (id) => {
    if (!window.confirm("Delete this invoice? Payments against it will also be removed.")) return;
    const { deleteWithUndo } = await import("@/lib/undoManager");
    try {
      await deleteWithUndo(
        () => api.delete(`/invoices/${id}`),
        { label: `Deleted invoice · you can undo`, onRestored: () => load() },
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [load]);

  const onPrint = useCallback((inv) => printInvoice(inv, company), [company]);

  const [uploadOpen, setUploadOpen] = useState(false);

  const ribbon = (
    <>
      <BcRibbonGroup label="New">
        <BcRibbonBtn
          icon={UploadSimple}
          label="Upload Invoice"
          variant="primary"
          onClick={() => setUploadOpen(true)}
          testid="ribbon-new-invoice"
        />
      </BcRibbonGroup>
      <BcRibbonGroup label="Process">
        <BcRibbonBtn
          icon={ArrowsClockwise}
          label="Refresh"
          onClick={load}
          testid="ribbon-refresh"
        />
      </BcRibbonGroup>
      <BcRibbonGroup label="Report">
        <CsvExport resource="invoices" filename="invoices.csv" render={({ onClick }) => (
          <BcRibbonBtn icon={FileArrowDown} label="Export CSV" onClick={onClick} testid="ribbon-export-csv" />
        )} />
      </BcRibbonGroup>
    </>
  );

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoices"
        subtitle="Upload Tally invoices · track payments and receivables."
        module="Sales · SD/AR"
        ribbon={ribbon}
      />
      <InvoiceUploadSheet
        projects={projects}
        company={company}
        onUploaded={load}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        hideTrigger
      />

      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            description="Generate your invoice in Tally, then click Upload Invoice to attach the PDF and record its details for AR tracking."
          />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="invoices-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Number</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((inv) => (
                  <InvoiceRow
                    key={inv.id}
                    inv={inv}
                    company={company}
                    expanded={expanded === inv.id}
                    onToggleExpand={() => setExpanded(expanded === inv.id ? null : inv.id)}
                    onReload={load}
                    onDownloadPdf={downloadPdf}
                    onPrint={onPrint}
                    onDelete={del}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
